// src/services/SubscriptionService.js - COMPLETE SUBSCRIPTION MANAGEMENT

import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebaseConfig';
import StorageService from './StorageService';

/**
 * Calculate subscription end date based on package duration
 * @param {Date} startDate - Subscription start date
 * @param {number} months - Number of months for subscription
 * @returns {string} - ISO string of end date
 */
export const calculateEndDate = (startDate = new Date(), months = 1) => {
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + months);
  return endDate.toISOString();
};

/**
 * Check if subscription is expiring soon (within 5 days)
 * @param {string} endDateISO - Subscription end date in ISO format
 * @returns {boolean} - True if expiring within 5 days
 */
export const isExpiringSoon = (endDateISO) => {
  if (!endDateISO) return false;
  
  const endDate = new Date(endDateISO);
  const today = new Date();
  const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  
  return daysUntilExpiry <= 5 && daysUntilExpiry > 0;
};

/**
 * Check if subscription has expired
 * @param {string} endDateISO - Subscription end date in ISO format
 * @returns {boolean} - True if expired
 */
export const isExpired = (endDateISO) => {
  if (!endDateISO) return false;
  
  const endDate = new Date(endDateISO);
  const today = new Date();
  
  return today > endDate;
};

/**
 * Get days remaining until expiry
 * @param {string} endDateISO - Subscription end date in ISO format
 * @returns {number} - Days remaining (negative if expired)
 */
export const getDaysRemaining = (endDateISO) => {
  if (!endDateISO) return null;
  
  const endDate = new Date(endDateISO);
  const today = new Date();
  
  return Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
};

/**
 * Check all restaurants for expired subscriptions and auto-deactivate
 * @returns {Promise<Object>} - Result with counts
 */
export const checkAndDeactivateExpiredSubscriptions = async () => {
  try {
     
    
    const data = await StorageService.getData();
    const restaurants = data.restaurants || [];
    
    let deactivatedCount = 0;
    let expiringSoonCount = 0;
    
    for (const restaurant of restaurants) {
      if (!restaurant.subscriptionEndDate) continue;
      
      const expired = isExpired(restaurant.subscriptionEndDate);
      const expiringSoon = isExpiringSoon(restaurant.subscriptionEndDate);
      
      // Auto-deactivate if expired and currently active
      if (expired && restaurant.status === 'Active') {
         
        
        const restaurantRef = doc(db, 'restaurants', restaurant.id);
        await setDoc(restaurantRef, {
          status: 'Inactive',
          deactivatedAt: new Date().toISOString(),
          deactivationReason: 'Subscription expired'
        }, { merge: true });
        
        deactivatedCount++;
      }
      
      if (expiringSoon) {
        expiringSoonCount++;
         
      }
    }
    
    // Clear cache to reflect changes
    if (deactivatedCount > 0) {
      StorageService.clearCache();
    }
    
     
    
    return {
      success: true,
      deactivatedCount,
      expiringSoonCount
    };
  } catch (error) {
    console.error('❌ Error checking subscriptions:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get all restaurants with expiring subscriptions (within 5 days)
 * @returns {Promise<Array>} - Array of restaurants expiring soon
 */
export const getExpiringSoonRestaurants = async () => {
  try {
    const data = await StorageService.getData();
    const restaurants = data.restaurants || [];
    
    return restaurants.filter(restaurant => 
      restaurant.subscriptionEndDate && 
      isExpiringSoon(restaurant.subscriptionEndDate) &&
      restaurant.status === 'Active'
    ).map(restaurant => ({
      id: restaurant.id,
      name: restaurant.name,
      subscriptionEndDate: restaurant.subscriptionEndDate,
      daysRemaining: getDaysRemaining(restaurant.subscriptionEndDate)
    }));
  } catch (error) {
    console.error('❌ Error getting expiring restaurants:', error);
    return [];
  }
};

/**
 * Extend subscription for a restaurant
 * @param {string} restaurantId - Restaurant ID
 * @param {number} months - Number of months to extend
 * @returns {Promise<Object>} - Result
 */
export const extendSubscription = async (restaurantId, months = 1) => {
  try {
    const data = await StorageService.getData();
    const restaurant = data.restaurants.find(r => r.id === restaurantId);
    
    if (!restaurant) {
      throw new Error('Restaurant not found');
    }
    
    // Calculate new end date from current end date or today
    const startDate = restaurant.subscriptionEndDate 
      ? new Date(restaurant.subscriptionEndDate)
      : new Date();
    
    const newEndDate = calculateEndDate(startDate, months);
    
    const restaurantRef = doc(db, 'restaurants', restaurantId);
    await setDoc(restaurantRef, {
      subscriptionEndDate: newEndDate,
      status: 'Active',
      lastRenewed: new Date().toISOString()
    }, { merge: true });
    
    StorageService.clearCache();
    
     
    
    return {
      success: true,
      newEndDate
    };
  } catch (error) {
    console.error('❌ Error extending subscription:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Default export
const SubscriptionService = {
  calculateEndDate,
  isExpiringSoon,
  isExpired,
  getDaysRemaining,
  checkAndDeactivateExpiredSubscriptions,
  getExpiringSoonRestaurants,
  extendSubscription
};

export default SubscriptionService;